import PropTypes from 'prop-types';
import React from 'react';

import { ApolloProvider } from '@apollo/client';
import { Provider } from 'react-redux';
import { merge, isEmpty, omit } from 'lodash';
import { Translations as FormTranslations } from '@sm360/widget-commons-forms';

import envConfig from 'vdp-used/config/index.js';
import {
    PurchaseMethods,
    Quote,
    Vehicle,
    PurchaseMethodsV25,
    PaymentOptionsMetadata,
} from '@sm360/widget-commons-vehicle';
import {
    ConfigsModel,
    ConfigsService as GeneralConfigService,
    ContentZonesService,
    LocaleService,
    UrlService,
    WindowProxy,
} from '@sm360/widget-commons-core-services';
import {
    EVENTS,
    HistoryService,
    ReduxClient,
    ApolloClient,
    VehiclesService,
    LocalStorageService,
    AdobeService,
    ADOBE_TAGS,
    AprService,
} from '@sm360/widget-commons-general';
import PaymentCalculatorDefault from '@sm360/widget-payment-calculator-v25';
import TradeIn from '@sm360/widget-trade-in';

import en from 'vdp-used/i18n/en.json';
import fr from 'vdp-used/i18n/fr.json';

// import VdpUsed from 'vdp-used/containers/VdpUsed/VdpUsed.js';
import VdpUsed from 'vdp-used/components/VdpUsed/VdpUsed.jsx';

import vdpUsedReducer from 'vdp-used/reducers/vdpUsedReducer.js';
import vehiclesReducer from 'vdp-used/reducers/vehiclesReducer.js';
import { setVehicle } from 'vdp-used/actions/vdpUsedActions.js';

const { TradeInVehicleActions } = Vehicle;

const { PurchaseMethodsActionsV25, PurchaseMethodsReducerV25 } = PurchaseMethodsV25;
const { PaymentOptionsMetadataActions, PaymentOptionsMetadataReducer } = PaymentOptionsMetadata;

class VdpUsedProvider extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            quoteId: UrlService.getUrlParameter('quoteId'),
            cashPurchaseOption: {},
            config: new ConfigsModel(),
        };

        merge(
            en,
            PurchaseMethods.Translations.en,
            Vehicle.Translation.en,
            Quote.Translation.en,
            FormTranslations.en,
            PaymentCalculatorDefault.translations.en,
            TradeIn.Translations.en
        );
        merge(
            fr,
            PurchaseMethods.Translations.fr,
            Vehicle.Translation.fr,
            Quote.Translation.fr,
            FormTranslations.fr,
            PaymentCalculatorDefault.translations.fr,
            TradeIn.Translations.fr
        );

        LocaleService.setLocale(props.locale);
        LocaleService.loadTranslations({ en, fr });

        GeneralConfigService.setWebsiteId(props.websiteId);
        GeneralConfigService.setLocation(props.location);
        GeneralConfigService.setOrganizationId(this.props.organizationId);
        GeneralConfigService.setOrganizationUnitId(this.props.organizationUnitId);
        GeneralConfigService.setOrganizationUnitLocation(this.props.organizationUnitLocation);
        GeneralConfigService.setIsInMobileApp(props.isInMobileApp);

        ContentZonesService.setImageServerBaseUrl(envConfig.source.image);
        ContentZonesService.addContentZones(props.contentZones);

        this.storeRedux = ReduxClient.getOrCreate();
        ReduxClient.addReducers({
            vdpUsed: vdpUsedReducer,
            vehicles: vehiclesReducer,
            tradeInVehicle: Vehicle.TradeInVehicleReducer,
            // Reducers for V2.5 migration
            purchaseMethodsV25: PurchaseMethodsReducerV25,
            vehicleV25: Vehicle.VehicleReducerV25,
            quoteV25: Quote.QuoteReducerV25,
            paymentOptionsMetadata: PaymentOptionsMetadataReducer,
        });

        this.checkTradeInVehicleStatus = this.checkTradeInVehicleStatus.bind(this);
        this.onTradeInCreated = this.onTradeInCreated.bind(this);
        this.onUnload = this.onUnload.bind(this);
    }

    componentDidMount() {
        if (this.state.quoteId) {
            const quote = this.storeRedux.getState()?.quoteV25;
            this.storeRedux.dispatch(Quote.QuoteActions.setQuoteV25(quote.set('id', this.state.quoteId)));
        }
        const tradeIn = parseInt(LocalStorageService.get('tradeIn') || 0, 10);
        const lien = parseInt(LocalStorageService.get('lien') || 0, 10);
        const cashDown = parseInt(LocalStorageService.get('cashDown') || 0, 10);

        VehiclesService.loadVehicle(
            this.props.vehicleId,
            'used',
            {
                location: this.props.organizationUnitLocation,
                orgId: this.props.organizationId,
                orgUnitId: this.props.organizationUnitId,
            },
            this.props.locale,
            { tradeIn, lien, cashDown, quoteId: this.state.quoteId }
        ).then(({ vehicle: vehicleData, paymentOptionsMetadata }) => {
            const vehicleModel = new Vehicle.VehicleModelV25(vehicleData);
            this.storeRedux.dispatch(setVehicle(vehicleModel));
            this.storeRedux.dispatch(PurchaseMethodsActionsV25.loadPurchaseMethodsSuccess(vehicleData.paymentOptions));
            this.setState({ cashPurchaseOption: vehicleData?.paymentOptions?.cashPurchase });

            const metaData = paymentOptionsMetadata.map((Data) => {
                return new PaymentOptionsMetadata.PaymentOptionsMetadataModel({
                    ...Data,
                });
            });
            this.storeRedux.dispatch(PaymentOptionsMetadataActions.loadPaymentOptionsMetadata(metaData));

            const config = new ConfigsModel({
                organizationUnitId: this.props.organizationUnitId,
                organizationUnitLocation: this.props.organizationUnitLocation,
                websiteId: this.props.websiteId,
            });
            this.setState({ config });
            if (this.props.hasAdobeTagging) {
                AdobeService.eventTrigger(
                    ADOBE_TAGS.VDP_LOAD(vehicleModel.bodyStyle?.name, {
                        make: vehicleData?.make?.name,
                        model: vehicleData?.model?.name,
                    }),
                    'si-details-load'
                );
            }
        });

        const $window = WindowProxy.get();

        if ($window) {
            $window.addEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);

            $window.addEventListener('beforeunload', this.onUnload);
        }
    }

    componentWillUnmount() {
        this.onUnload();
    }

    onUnload() {
        const $window = WindowProxy.get();

        if ($window) {
            $window.removeEventListener(EVENTS.TRADEIN_CREATED, this.onTradeInCreated);

            HistoryService.add(WindowProxy.location().href);
        }
    }

    onTradeInCreated() {
        this.checkTradeInVehicleStatus(true);
    }

    checkTradeInVehicleStatus(forceLoad) {
        const storeState = this.storeRedux.getState();
        if (forceLoad || isEmpty(storeState.tradeInVehicle) || !storeState.tradeInVehicle.id) {
            this.storeRedux.dispatch(TradeInVehicleActions.loadTradeInVehicle(storeState.tradeInVehicle));
        }
    }

    render() {
        const config = this.state.config?.getConfig();
        const orgUnitId = config?.orgUnitId || GeneralConfigService.getOrganizationUnitId();
        const location = config?.location || GeneralConfigService?.getOrganizationUnitLocation();
        const displayCreditRate = AprService.displayApr(location, orgUnitId);
        return (
            <ApolloProvider client={ApolloClient.getOrCreate()}>
                <Provider store={this.storeRedux}>
                    <VdpUsed
                        // Carproof was bought by Carfax and we renamed all instances of "carproof" in this project to
                        // "carfax", but we still receive carproof from the backend.
                        {...omit(this.props, ['carproofKey', 'carproofLeadGenerator'])}
                        carfaxKey={this.props.carproofKey}
                        carfaxLeadGenerator={this.props.carproofLeadGenerator}
                        quoteId={this.state.quoteId}
                        isShowroomV2Enabled={this.props.isShowroomV2Enabled}
                        cashPurchaseOption={this.state.cashPurchaseOption}
                        organizationId={this.props.organizationId}
                        config={config}
                        showCarfax={this.props.showCarfax}
                        hasAdobeTagging={this.props.hasAdobeTagging}
                        displayCreditRate={displayCreditRate}
                        websiteLogo={this.props?.websiteLogo}
                        websiteDomain={this.props?.websiteDomain}
                        organizationUnitPhone={this.props?.organizationUnitPhone}
                        websiteFacebookPageUrl={this.props?.websiteFacebookPageUrl}
                        websiteInstagramClientId={this.props?.websiteInstagramClientId}
                        websiteInstagramUserId={this.props?.websiteInstagramUserId}
                        websiteTwitterUrl={this.props?.websiteTwitterUrl}
                        websiteYoutubeUrl={this.props?.websiteYoutubeUrl}
                        organizationUnitName={this.props?.organizationUnitName}
                        motoInsightUsedEnabled={this.props.motoInsightUsedEnabled}
                        roadsterUsedEnabled={this.props.roadsterUsedEnabled}
                        eshopUsedEnabled={this.props.eshopUsedEnabled}
                        shopClickDriveUsedEnabled={this.props.shopClickDriveUsedEnabled}
                        roadsterLink={this.props.roadsterLink}
                        dealerCode={this.props.dealerCode}
                        dealerBAC={this.props.dealerBAC}
                    />
                </Provider>
            </ApolloProvider>
        );
    }
}

VdpUsedProvider.defaultProps = {
    isInMobileApp: false,
    carproofKey: '',
    carproofLeadGenerator: false,
    showCarfax: true, // Default set to true if props is not available, otherwise flag is available in XMS website management
    hasAdobeTagging: false,
    websiteLogo: null,
    websiteDomain: null,
    organizationUnitPhone: null,
    websiteFacebookPageUrl: null,
    websiteInstagramClientId: null,
    websiteInstagramUserId: null,
    websiteTwitterUrl: null,
    websiteYoutubeUrl: null,
    organizationUnitName: null,
    motoInsightUsedEnabled: false,
    roadsterUsedEnabled: false,
    eshopUsedEnabled: false,
    shopClickDriveUsedEnabled: false,
    roadsterLink: '',
    dealerCode: '',
    dealerBAC: '',
};

VdpUsedProvider.propTypes = {
    contentZones: PropTypes.objectOf(
        PropTypes.arrayOf(
            PropTypes.shape({
                active: PropTypes.bool,
                group: PropTypes.string,
                id: PropTypes.number,
                location: PropTypes.string,
                position: PropTypes.number,
                type: PropTypes.string,
                value: PropTypes.string,
            })
        )
    ).isRequired,
    isInMobileApp: PropTypes.bool,
    locale: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    organizationId: PropTypes.number.isRequired,
    organizationUnitId: PropTypes.number.isRequired,
    organizationUnitLocation: PropTypes.string.isRequired,
    vehicleId: PropTypes.number.isRequired,
    websiteId: PropTypes.number.isRequired,
    carproofKey: PropTypes.string,
    carproofLeadGenerator: PropTypes.bool,
    isShowroomV2Enabled: PropTypes.bool.isRequired,
    showCarfax: PropTypes.bool.isRequired,
    hasAdobeTagging: PropTypes.bool,
    websiteLogo: PropTypes.string.isRequired,
    websiteDomain: PropTypes.string.isRequired,
    organizationUnitPhone: PropTypes.string.isRequired,
    websiteFacebookPageUrl: PropTypes.string.isRequired,
    websiteInstagramClientId: PropTypes.string.isRequired,
    websiteInstagramUserId: PropTypes.string.isRequired,
    websiteTwitterUrl: PropTypes.string.isRequired,
    websiteYoutubeUrl: PropTypes.string.isRequired,
    organizationUnitName: PropTypes.string.isRequired,
    motoInsightUsedEnabled: PropTypes.bool,
    roadsterUsedEnabled: PropTypes.bool,
    eshopUsedEnabled: PropTypes.bool,
    shopClickDriveUsedEnabled: PropTypes.bool,
    roadsterLink: PropTypes.string,
    dealerCode: PropTypes.string,
    dealerBAC: PropTypes.string,
};

VdpUsedProvider.displayName = 'VdpUsedProvider';

export default VdpUsedProvider;
